import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { useResources } from '@ctw/shared/context/_internal/resource/resource-fetch';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import {
  type FetchableModelType,
  type FetchableResourceType,
  isFetchableResourceType,
} from '@ctw/shared/utils/fhir/resource-mappings';
import { useMemo } from 'react';

export type EvidenceModel = FetchableModelType<FetchableResourceType>;

export const EVIDENCE_SECTION_ORDER = [
  'Observations',
  'Diagnostic Reports',
  'Medications',
  'Documents',
  'Diagnoses',
] as const;

export type EvidenceSectionKey = (typeof EVIDENCE_SECTION_ORDER)[number];

export const RESOURCE_TYPE_TO_SECTION: Record<string, EvidenceSectionKey> = {
  Observation: 'Observations',
  DiagnosticReport: 'Diagnostic Reports',
  MedicationStatement: 'Medications',
  DocumentReference: 'Documents',
  Condition: 'Diagnoses',
};

const RESOURCE_TYPE_ORDER: Record<string, number> = Object.fromEntries(
  Object.entries(RESOURCE_TYPE_TO_SECTION).map(([type, section]) => [
    type,
    EVIDENCE_SECTION_ORDER.indexOf(section),
  ]),
);

export function useEvidenceResources(riskGap: RiskGapModel) {
  const { patient } = usePatientContext();

  const identifiers = useMemo(
    () =>
      riskGap.evidenceReferences
        .filter((ref) => isFetchableResourceType(ref.resourceType))
        .map((ref) => ({
          resourceId: ref.resourceId,
          resourceType: ref.resourceType as FetchableResourceType,
        })),
    [riskGap.evidenceReferences],
  );

  const { data: resources = [], isLoading } = useResources<EvidenceModel>({
    identifiers,
    patient,
  });

  const resourceById = useMemo(() => {
    const map = new Map<string, EvidenceModel>();
    for (const resource of resources) {
      map.set(resource.id, resource);
    }
    return map;
  }, [resources]);

  const sortedReferences = useMemo(
    () =>
      [...riskGap.evidenceReferences].sort(
        (a, b) =>
          (RESOURCE_TYPE_ORDER[a.resourceType] ?? 5) - (RESOURCE_TYPE_ORDER[b.resourceType] ?? 5),
      ),
    [riskGap.evidenceReferences],
  );

  const referenceDisplayByResourceId = useMemo(() => {
    const map = new Map<string, string>();
    for (const ref of riskGap.evidenceReferences) {
      if (ref.display) {
        map.set(ref.resourceId, ref.display);
      }
    }
    return map;
  }, [riskGap.evidenceReferences]);

  return {
    resources,
    resourceById,
    sortedReferences,
    referenceDisplayByResourceId,
    isLoading,
    hasEvidence: identifiers.length > 0,
  };
}

export type UseEvidenceResourcesResult = ReturnType<typeof useEvidenceResources>;
